/**
 * Поле «Тип» позиции бюджета — выпадашка вместо свободного текста (AK-656).
 *
 * UAT founder 18.08: «тип» набирали руками, и один и тот же гербицид приезжал
 * то «гербицид», то «Гербициды», то «герб.» — справочник рассыпался на
 * синонимы ещё до первого отчёта.
 *
 * Три решения, которые тут закреплены:
 *
 * 1. **Алфавит + поиск, без «топ-N»** — сквозное правило выпадашек проекта.
 *    Ранжирование общее с единицами измерения (`searchRank`): точное совпадение
 *    и начало слова выше, чем буква где-то в середине.
 * 2. **Creatable.** Любой свой тип вводится строкой «Использовать своё: …».
 *    Список — подсказка, а не решётка: нестандартную позицию он не блокирует.
 *    Поэтому `item_kind` в БД остаётся текстом и миграции не нужно.
 * 3. **Канон, где он есть.** У СЗР перечень назначений живёт в платформенном
 *    справочнике `reference.pesticide_categories` (гербицид / фунгицид / …) —
 *    берём его, а не выдумываем свой. Словарь раздела остаётся запасным на
 *    случай пустого/недоступного каталога, чтобы поле не осталось голым.
 *    Препаративные формы (КЭ, ВДГ) — ДРУГАЯ сущность, сюда не идут.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SEARCH_RANK_NONE, searchRank } from '@/lib/reference/normalizeSearch';
import { useSubCatalog } from '@/lib/pesticides/queries';
import { SECTION_ITEM_KINDS, type BudgetSection } from '@/lib/planning/budgetCatalog';
import { cn } from '@/lib/utils';

/** Есть ли у раздела список типов (иначе поле остаётся текстовым). */
export function hasItemKindOptions(section: BudgetSection): boolean {
  return (SECTION_ITEM_KINDS[section]?.length ?? 0) > 0;
}

/**
 * Собрать список: канон + словарь раздела + уже сохранённое значение позиции.
 *
 * Дубли гасим по нормализованному написанию (регистр и «ё» не считаются
 * разницей) — иначе «Гербицид» из каталога и «гербицид» из словаря встали бы
 * двумя строками. Сортировка — алфавит по-русски.
 */
export function itemKindOptions(
  section: BudgetSection,
  canonical: readonly string[],
  current: string,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of [...canonical, ...(SECTION_ITEM_KINDS[section] ?? []), current]) {
    const value = raw.trim();
    const key = value.toLowerCase().replace(/ё/g, 'е');
    if (!value || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out.sort((a, b) => a.localeCompare(b, 'ru'));
}

export function ItemKindSelect({
  section,
  value,
  onChange,
  label,
}: {
  section: BudgetSection;
  value: string;
  onChange: (kind: string) => void;
  /** Подпись типа для этого раздела — «Тип», «Группа работ», «Вид услуги». */
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  // Канон только там, где он реально есть — за удобрениями и ГСМ платформенного
  // перечня назначений нет, и дёргать чужой эндпоинт «на всякий случай» незачем.
  const categories = useSubCatalog('categories', { enabled: section === 'pesticide' });
  const canonical = useMemo(
    () => (section === 'pesticide' ? (categories.data ?? []).map((c) => c.name) : []),
    [section, categories.data],
  );

  const options = useMemo(() => {
    const all = itemKindOptions(section, canonical, value);
    return all
      .map((kind) => ({ kind, rank: searchRank(term, kind) }))
      .filter((row) => row.rank !== SEARCH_RANK_NONE)
      .sort((a, b) => a.rank - b.rank || a.kind.localeCompare(b.kind, 'ru'))
      .map((row) => row.kind);
  }, [section, canonical, value, term]);

  const custom = term.trim();
  const canCreate =
    custom.length > 0 &&
    !options.some((o) => o.toLowerCase().replace(/ё/g, 'е') === custom.toLowerCase().replace(/ё/g, 'е'));

  const pick = (kind: string) => {
    onChange(kind === value ? '' : kind);
    setTerm('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between font-normal"
          aria-label={label}
        >
          <span className={cn(!value && 'text-muted-foreground')}>{value || `${label}…`}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="relative border-b border-border p-2">
          <Search className="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Поиск или свой вариант"
            className="h-8 pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {options.length === 0 && !canCreate && (
            <p className="p-2 text-sm text-muted-foreground">Ничего не нашлось</p>
          )}
          {options.map((kind) => (
            <button
              key={kind}
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
              onClick={() => pick(kind)}
            >
              <Check className={cn('h-4 w-4', kind === value ? 'opacity-100' : 'opacity-0')} />
              {kind}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
              onClick={() => pick(custom)}
            >
              <Plus className="h-4 w-4" />
              Использовать своё: «{custom}»
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
